import { useState, useMemo, useRef, useLayoutEffect, memo } from 'react';
import styled from '@emotion/styled';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { theme, scaled } from '../styles/theme';
import { Card, ChartCardTitle, ToggleGroup, ToggleButton } from '../styles/components';
import { getTypeColor, ALL_INVESTMENT_TYPES, PHYSICAL_TYPES, FINANCIAL_TYPES } from '../utils/colors';
import { formatInvestmentVolume } from '../utils/formatters';
import { getLocale, typeLabel, useT } from '../i18n';
import type { ChartBucket } from '../types';

// Gap (px) between the widest y label and the axis line. The y-axis column
// width is measured from the rendered labels + this gap, so the axis hugs the
// labels (rather than a fixed wide gutter) while the labels stay left-aligned
// at the chart's left edge.
const Y_AXIS_LABEL_GAP = 6;
const Y_AXIS_WIDTH_FALLBACK = 40;

type Mode = 'count' | 'volume';

interface Props {
  chartData: ChartBucket[];
  investmentTypeFilter: string[];
  activeTiers: { physical: boolean; financial: boolean };
  onTypeFilterChange: (types: string[]) => void;
}

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 500px;
  /* Flat on the page background — no tile shadow. */
  box-shadow: none;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`;

const ChartWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

// Holds the ResponsiveContainer; flex:1 so the plot fills the space left
// under the unit caption.
const ChartArea = styled.div`
  flex: 1;
  min-height: 0;
`;

// Unit caption above the y-axis (budget chart parity): sits at the plot's
// left edge — i.e. just right of the y-axis tick labels, top-aligned.
const AxisUnitLabel = styled.div`
  font-family: var(--sans-stack);
  font-size: ${scaled(13)};
  color: #000000;
  text-align: left;
  white-space: nowrap;
  margin-bottom: 2px;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.muted};
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
`;

function formatValue(value: number, mode: Mode): string {
  if (mode === 'count') return String(value);
  return formatInvestmentVolume(value);
}

const tooltipContainerStyle: React.CSSProperties = {
  background: '#fff',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: theme.borderRadius.md,
  padding: '10px 14px',
  fontFamily: 'var(--sans-stack)',
  fontSize: theme.fontSize.sm,
  boxShadow: 'var(--shadow-m)',
  minWidth: 160,
};

const tooltipLabelStyle: React.CSSProperties = {
  fontWeight: theme.fontWeight.semibold,
  marginBottom: 8,
  color: theme.colors.text.primary,
};

const tooltipRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 16,
  padding: '2px 0',
};

const tooltipNameStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  color: theme.colors.text.secondary,
};

const tooltipDotBase: React.CSSProperties = {
  width: 10,
  height: 10,
  borderRadius: '50%',
  display: 'inline-block',
  flexShrink: 0,
};

const tooltipValueStyle: React.CSSProperties = {
  fontWeight: theme.fontWeight.medium,
  color: theme.colors.text.primary,
};

const tooltipTotalStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  borderTop: `1px solid ${theme.colors.border}`,
  marginTop: 6,
  paddingTop: 6,
  fontWeight: theme.fontWeight.semibold,
  color: theme.colors.text.primary,
};

function CustomTooltip({ active, payload, label, mode }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  mode: Mode;
}) {
  const m = useT();
  if (!active || !payload?.length) return null;

  const items = payload.filter(p => p.value > 0);
  if (items.length === 0) return null;

  const total = items.reduce((sum, p) => sum + p.value, 0);

  return (
    <div style={tooltipContainerStyle}>
      <div style={tooltipLabelStyle}>
        {label}
      </div>
      {items.map(item => (
        <div key={item.name} style={tooltipRowStyle}>
          <span style={tooltipNameStyle}>
            <span style={{ ...tooltipDotBase, background: item.color }} />
            {/* Bar name is the stored type; show its display label. */}
            {typeLabel(item.name.replace(/_volume$/, ''))}
          </span>
          <span style={tooltipValueStyle}>
            {formatValue(item.value, mode)}
          </span>
        </div>
      ))}
      {items.length > 1 && (
        <div style={tooltipTotalStyle}>
          <span>{m.charts.total}</span>
          <span>{formatValue(total, mode)}</span>
        </div>
      )}
    </div>
  );
}

// Matches the RankedBarChart legend (DZ template): white pill chips with a
// 10px round colour dot, wrapping centred below the chart.
const TypeLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  padding-top: 16px;
  font-family: var(--sans-stack);
  font-size: ${theme.fontSize.sm};
`;

const TypeLegendItem = styled.span<{ dimmed?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 19px;
  background: #ffffff;
  cursor: pointer;
  color: ${theme.colors.text.primary};
  opacity: ${p => p.dimmed ? 0.4 : 1};
  transition: opacity 0.15s;
  &:hover { opacity: ${p => p.dimmed ? 0.65 : 0.85}; }
`;

const TypeLegendDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.color};
`;

export const TimeSeriesChart = memo(function TimeSeriesChart({ chartData: buckets, investmentTypeFilter, activeTiers, onTypeFilterChange }: Props) {
  const m = useT();
  const [mode, setMode] = useState<Mode>('volume');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [yAxisWidth, setYAxisWidth] = useState(Y_AXIS_WIDTH_FALLBACK);

  const { data: rechartsData, types } = useMemo(() => {
    if (buckets.length === 0) return { data: [], types: [] };

    // Collect distinct types and pivot into recharts format
    const typesSet = new Set<string>();
    const quarterMap = new Map<string, Record<string, { count: number; volume: number }>>();

    for (const b of buckets) {
      typesSet.add(b.type);
      const key = `${b.year}-Q${b.quarter}`;
      if (!quarterMap.has(key)) quarterMap.set(key, {});
      const qd = quarterMap.get(key)!;
      qd[b.type] = { count: b.count, volume: b.volume };
    }

    const sortedTypes = [...typesSet].sort((a, b) => {
      if (a === 'Other' || a === 'Sonstige') return 1;
      if (b === 'Other' || b === 'Sonstige') return -1;
      const ia = ALL_INVESTMENT_TYPES.indexOf(a);
      const ib = ALL_INVESTMENT_TYPES.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

    // Fill gaps by quarter
    const keys = [...quarterMap.keys()].sort();
    if (keys.length === 0) return { data: [], types: sortedTypes };

    const [minKey, maxKey] = [keys[0]!, keys[keys.length - 1]!];
    const minY = Number(minKey.split('-')[0]);
    const minQ = Number(minKey.split('Q')[1]);
    const maxY = Number(maxKey.split('-')[0]);
    const maxQ = Number(maxKey.split('Q')[1]);

    const result: Record<string, unknown>[] = [];
    let y = minY;
    let q = minQ;
    while (y < maxY || (y === maxY && q <= maxQ)) {
      const key = `${y}-Q${q}`;
      const label = new Date(y, (q - 1) * 3).toLocaleDateString(getLocale(), { month: 'short', year: 'numeric' });
      const row: Record<string, unknown> = { key, label };
      const quarterData = quarterMap.get(key);
      for (const type of sortedTypes) {
        const td = quarterData?.[type];
        row[type] = td?.count ?? 0;
        row[`${type}_volume`] = td?.volume ?? 0;
      }
      result.push(row);
      q++;
      if (q > 4) { q = 1; y++; }
    }
    return { data: result, types: sortedTypes };
  }, [buckets]);

  const { divisor, label: unitLabel, decimals } = useMemo(() => {
    if (mode !== 'volume' || rechartsData.length === 0) return { divisor: 1, label: '', decimals: 0 };
    let maxStacked = 0;
    for (const row of rechartsData) {
      let sum = 0;
      for (const type of types) {
        sum += (row[`${type}_volume`] as number) || 0;
      }
      if (sum > maxStacked) maxStacked = sum;
    }
    const units = m.charts.axisUnit;
    let divisor = 1, label = units.one;
    if (maxStacked >= 1_000_000_000) { divisor = 1_000_000_000; label = units.bn; }
    else if (maxStacked >= 1_000_000) { divisor = 1_000_000; label = units.m; }
    else if (maxStacked >= 1_000) { divisor = 1_000; label = units.k; }
    // One decimal count for the WHOLE axis (derived from the largest value, so
    // every tick — including 0 — shows the same number of decimals). divisor
    // normalises the max into [1, 1000), so this is 0, 1, or 2 places.
    const maxDiv = maxStacked / divisor;
    const decimals = maxDiv >= 100 ? 0 : maxDiv >= 10 ? 1 : 2;
    return { divisor, label, decimals };
  }, [rechartsData, types, mode, m]);

  const unitText = mode === 'count' ? m.charts.count : unitLabel;
  // X labels at a uniform, whole-year cadence. The data is strictly quarterly
  // (gap-filled), so stepping by a multiple of 4 bars guarantees every shown
  // label falls on the SAME month, evenly spaced. The year step grows so at
  // most ~8 labels show on long ranges (still same month, still uniform).
  const xTickInterval = useMemo(() => {
    const years = Math.max(1, Math.ceil(rechartsData.length / 4));
    const yearStep = Math.max(1, Math.ceil(years / 8));
    return yearStep * 4 - 1;
  }, [rechartsData.length]);

  // Size the y-axis column to the rendered labels (+ a small gap) so the axis
  // sits right next to the left-aligned labels. recharts v3 renders the y tick
  // labels in `.recharts-yAxis-tick-labels`; measure after commit and feed the
  // width back. Labels are anchored at x=0, so width changes only move the
  // axis, never the label alignment — the feedback settles in one extra frame.
  useLayoutEffect(() => {
    let raf = 0;
    const measure = (): boolean => {
      const ticks = chartContainerRef.current?.querySelectorAll<SVGGraphicsElement>(
        '.recharts-yAxis-tick-labels text',
      );
      if (!ticks || ticks.length === 0) return false;
      let max = 0;
      ticks.forEach(t => { const w = t.getBBox().width; if (w > max) max = w; });
      const next = Math.ceil(max) + Y_AXIS_LABEL_GAP;
      setYAxisWidth(prev => (Math.abs(prev - next) > 0.5 ? next : prev));
      return true;
    };
    if (!measure()) {
      let tries = 0;
      const retry = () => { if (!measure() && tries++ < 20) raf = requestAnimationFrame(retry); };
      raf = requestAnimationFrame(retry);
    }
    return () => cancelAnimationFrame(raf);
  }, [rechartsData, mode, divisor]);

  return (
    <ChartCard>
      <ChartHeader>
        <ChartCardTitle>{m.app.timeline}</ChartCardTitle>
        <ToggleGroup>
          <ToggleButton active={mode === 'count'} onClick={() => setMode('count')}>
            {m.charts.count}
          </ToggleButton>
          <ToggleButton active={mode === 'volume'} onClick={() => setMode('volume')}>
            {m.charts.volume}
          </ToggleButton>
        </ToggleGroup>
      </ChartHeader>

      {rechartsData.length === 0 ? (
        <EmptyState>{m.charts.noData}</EmptyState>
      ) : (<>
        <ChartWrapper ref={chartContainerRef}>
          {/* Unit caption starts at the plot's left edge (= just right of the
              y-axis labels), top-aligned — matches the budget chart. */}
          <AxisUnitLabel style={{ paddingLeft: yAxisWidth }}>{unitText}</AxisUnitLabel>
          <ChartArea>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rechartsData} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                {/* Figma-aligned axis chrome — black axes, dashed black grid
                    at 30 % opacity, no tick line. Matches the Budget charts. */}
                <CartesianGrid strokeDasharray="3 3" stroke="#000000" strokeOpacity={0.3} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fontFamily: 'var(--sans-stack)', fill: '#000000' }}
                  tickLine={false}
                  axisLine={{ stroke: '#000000' }}
                  /* Labels stay horizontal and appear at a fixed whole-year
                     cadence (interval is a multiple of 4 quarters), so every
                     shown label is the same month and uniformly spaced. */
                  interval={xTickInterval}
                  height={28}
                  tickMargin={8}
                />
                <YAxis
                  allowDecimals={mode !== 'count'}
                  tickLine={false}
                  axisLine={{ stroke: '#000000' }}
                  width={yAxisWidth}
                  /* Custom tick rendered LEFT-aligned at x=0 (the chart/title
                     left edge, since margin.left=0), so every label — even the
                     short "0" — lines up flush under the chart title. */
                  tick={({ y, payload }: { y?: number | string; payload?: { value?: number } }) => {
                    const value = Number(payload?.value ?? 0);
                    return (
                      <text
                        x={0}
                        y={Number(y) || 0}
                        dy="0.32em"
                        textAnchor="start"
                        fontSize={11}
                        fontFamily="var(--sans-stack)"
                        fill="#000000"
                      >
                        {mode === 'volume'
                        ? (value / divisor).toLocaleString(getLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
                        : value}
                      </text>
                    );
                  }}
                />
                <Tooltip
                  content={<CustomTooltip mode={mode} />}
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                />
                {/* Legend rendered outside chart below */}
                {types.map(type => (
                  <Bar
                    key={type}
                    dataKey={mode === 'count' ? type : `${type}_volume`}
                    name={type}
                    fill={getTypeColor(type)}
                    stackId="stack"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartArea>
        </ChartWrapper>
        <TypeLegend>
          {ALL_INVESTMENT_TYPES.filter(t => {
            if (PHYSICAL_TYPES.includes(t)) return activeTiers.physical;
            if (FINANCIAL_TYPES.includes(t)) return activeTiers.financial;
            return false;
          }).map(type => {
            const isActive = investmentTypeFilter.length === 0 || investmentTypeFilter.includes(type);
            return (
              <TypeLegendItem
                key={type}
                dimmed={!isActive}
                onClick={() => {
                  const tierTypes = ALL_INVESTMENT_TYPES.filter(t =>
                    (activeTiers.physical && PHYSICAL_TYPES.includes(t)) || (activeTiers.financial && FINANCIAL_TYPES.includes(t))
                  );
                  const allActive = investmentTypeFilter.length === 0 || tierTypes.every(t => investmentTypeFilter.includes(t));
                  if (allActive) {
                    onTypeFilterChange([type]);
                  } else if (investmentTypeFilter.includes(type)) {
                    const next = investmentTypeFilter.filter(t => t !== type);
                    onTypeFilterChange(next.length > 0 ? next : tierTypes);
                  } else {
                    const next = [...investmentTypeFilter, type];
                    const allBack = tierTypes.every(t => next.includes(t));
                    onTypeFilterChange(allBack ? tierTypes : next);
                  }
                }}
              >
                <TypeLegendDot color={getTypeColor(type)} />
                {typeLabel(type)}
              </TypeLegendItem>
            );
          })}
        </TypeLegend>
      </>)}
    </ChartCard>
  );
});
